import { INLINE_LINK, LinkPanel } from '@/components/bedrock';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { PageHero } from '@/components/marketing-ui';
import { useT } from '@/core/I18nProvider';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import type { ReactElement } from 'react';

/**
 * Legal index — `/legal`.
 *
 * One page that fronts every binding legal document, so the footer can carry a
 * single "Legal" link instead of enumerating the documents in a nine-row
 * column. The cookie policy is listed here too (it IS a legal document) while
 * also keeping its own footer link: it is the ePrivacy surface a visitor looks
 * for by name, next to the consent banner that references it.
 *
 * **Six documents, not ten.** The certifications page, the accessibility
 * statement and the vulnerability-disclosure policy are STATEMENTS about how
 * the service is operated, not instruments a customer is bound by, so they
 * belong to the Trust Center and the intro sends readers there. Listing them
 * here as well would make two hubs that each look like the whole answer.
 *
 * Deliberately the same card-grid shape as the Trust Center hub (`TrustHub`):
 * a reader arrives asking one question ("where are the terms?"), and six
 * parallel items with one link each is a grid. `LinkPanel` makes the whole
 * card the target per `ui-design.md` § 17's simple shape — nothing else inside
 * a card is interactive.
 *
 * This is an INDEX, not an instrument: it states no legal position of its own,
 * so it carries no `LegalTranslationNotice` and no last-updated stamp. Each
 * document dates itself.
 */
const LEGAL_DOCUMENTS = [
  { id: 'terms', to: '/terms' },
  { id: 'privacy', to: '/trust/privacy' },
  { id: 'dpa', to: '/trust/dpa' },
  { id: 'subprocessors', to: '/trust/subprocessors' },
  { id: 'cookies', to: '/cookies' },
  { id: 'refunds', to: '/trust/refunds' },
] as const;

export function Legal(): ReactElement {
  const { t } = useT();
  useLocalizedSeoMeta({
    title: t('legal.meta.title'),
    description: t('legal.meta.description'),
    path: '/legal',
  });
  return (
    <main id="main" tabIndex={-1} className="bg-[color:var(--bd-page)]">
      <PageHero
        align="start"
        width="prose"
        title={t('legal.title')}
        description={
          <>
            {t('legal.intro.before')}{' '}
            <Link className={INLINE_LINK} to="/trust">
              {t('legal.intro.link')}
            </Link>
            {t('legal.intro.after')}
          </>
        }
      />

      <div className="mx-auto max-w-3xl px-6 pb-20 pt-10 text-[1.02rem] leading-[1.7] text-[color:var(--bd-body)]">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {LEGAL_DOCUMENTS.map((doc) => (
            <section key={doc.id} aria-labelledby={`legal-${doc.id}`}>
              <LinkPanel href={doc.to} className="flex h-full flex-col gap-2 p-5">
                <h2
                  id={`legal-${doc.id}`}
                  className="scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.12rem] font-semibold leading-[1.2] tracking-[-0.02em] text-[color:var(--bd-ink)]"
                >
                  {t(`legal.${doc.id}.heading`)}
                </h2>
                <p className="text-[0.94rem] leading-[1.62] text-[color:var(--bd-muted)]">
                  {t(`legal.${doc.id}.body`)}
                </p>
                {/* The card is the link, so this is the AFFORDANCE rather than a
                    second control. The arrow lives in the COPY (not `<CtaArrow>`)
                    so `link-affordance-arrows.test.ts` keeps it mirrored in
                    Arabic and Hebrew — the same convention as the trust hub. */}
                <span className="mt-auto pt-1 text-[0.9rem] font-medium text-[color:var(--bd-link)]">
                  {t(`legal.${doc.id}.link`)}
                </span>
              </LinkPanel>
            </section>
          ))}
        </div>
      </div>
    </main>
  );
}
